import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, MapPin, Users, Ruler, Star, ArrowRight } from 'lucide-react';
import { getIcon } from '../data/iconMap';
import {
  getCategory,
  getSubcategory,
  categoryUrl,
  SHOP_ITEMS,
  SERVICE_VENDORS,
} from '../data/categories';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subcategory listing page.
 * - For yachts-for-sale / yacht-charters: pulls real yachts from API and filters.
 * - For shop / services: renders placeholder product / vendor data.
 */
export default function CategoryListing() {
  const { categorySlug, subSlug } = useParams();
  const category = getCategory(categorySlug);
  const sub = getSubcategory(categorySlug, subSlug);

  usePageTitle(sub ? `${sub.name} — ${category?.title || ''}` : category?.title || 'Category');

  if (!category || !sub) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-4xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Subcategory not found
          </h1>
          <Link to="/" className="text-gold uppercase tracking-[0.2em] text-sm">← Back home</Link>
        </div>
      </div>
    );
  }

  const isYacht = categorySlug === 'yachts-for-sale' || categorySlug === 'yacht-charters';

  return (
    <div className="min-h-screen" data-testid={`subcategory-${categorySlug}-${subSlug}`}>
      <Hero category={category} sub={sub} />
      {isYacht ? (
        <YachtListingBody category={category} sub={sub} />
      ) : categorySlug === 'shop' ? (
        <ProductListingBody sub={sub} />
      ) : (
        <ServiceListingBody sub={sub} />
      )}
    </div>
  );
}

// ---------- Shared Hero ---------------------------------------------------

function Hero({ category, sub }) {
  const SubIcon = getIcon(sub.icon);
  return (
    <section className="relative h-[46vh] min-h-[340px] flex items-end overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{ backgroundImage: `url('${sub.image || category.hero}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/75 to-[#050C16]/20" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pb-12 w-full">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 mb-6">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={12} />
          <Link to={categoryUrl(category.slug)} className="hover:text-gold">{category.title}</Link>
          <ChevronRight size={12} />
          <span className="text-gold">{sub.name}</span>
        </div>
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 border border-gold/50 flex items-center justify-center text-gold">
            <SubIcon size={18} />
          </span>
          <div>
            <h1
              className="text-4xl sm:text-5xl font-medium text-white tracking-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="subcategory-title"
            >
              {sub.name}
            </h1>
            {sub.description && (
              <p className="text-white/70 mt-3 max-w-2xl">{sub.description}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Yacht body (live data) ----------------------------------------

function YachtListingBody({ category, sub }) {
  const [yachts, setYachts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios
      .get(`${API_URL}/api/yachts`)
      .then((r) => setYachts(r.data))
      .catch(() => setYachts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = yachts.filter((y) => {
    if (category.listingType && y.listing_type !== category.listingType) return false;
    const m = sub.match || {};
    if (m.yacht_type && y.yacht_type !== m.yacht_type) return false;
    if (m.min_length_ft && y.length_ft < m.min_length_ft) return false;
    if (m.max_length_ft && y.length_ft > m.max_length_ft) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !y.name?.toLowerCase().includes(q) &&
        !y.builder?.toLowerCase().includes(q) &&
        !y.location?.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
        <Input
          placeholder="Search by name, builder or location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-transparent border-white/20 text-white max-w-md"
          data-testid="subcategory-search"
        />
        <p className="text-white/60 text-sm">
          {loading ? 'Loading…' : `${filtered.length} result${filtered.length === 1 ? '' : 's'}`}
        </p>
      </div>

      {filtered.length === 0 && !loading ? (
        <EmptyState category={category} sub={sub} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="yacht-result-grid">
          {filtered.map((y) => (
            <Link
              key={y.yacht_id}
              to={`/yacht/${y.yacht_id}`}
              className="group relative overflow-hidden glass-light border border-white/5 hover:border-gold/40 transition-all duration-500 h-[460px]"
              data-testid={`yacht-card-${y.yacht_id}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url('${y.images?.[0]}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/60 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-7">
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">{y.builder}</p>
                <h3
                  className="text-2xl font-medium text-white mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {y.name}
                </h3>
                <div className="flex items-center gap-4 text-xs text-white/60 mb-3">
                  <span className="inline-flex items-center gap-1"><Ruler size={12} />{y.length_ft}ft</span>
                  <span className="inline-flex items-center gap-1"><Users size={12} />{y.guest_capacity}</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={12} />{y.location}</span>
                </div>
                <p className="text-gold font-semibold">
                  {y.charter_weekly_usd
                    ? `$${y.charter_weekly_usd.toLocaleString()} / week`
                    : y.price_usd
                    ? `$${y.price_usd.toLocaleString()}`
                    : 'Price on application'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Product body (shop placeholder data) --------------------------

function ProductListingBody({ sub }) {
  const items = SHOP_ITEMS[sub.slug] || [];
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
        <p className="text-white/60 text-sm">
          {items.length} products · Ships across Southeast Asia
        </p>
        <div className="flex gap-3">
          <Button variant="ghost" className="text-white/70 hover:text-gold text-xs uppercase tracking-[0.2em]">
            Sort: Featured
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="product-grid">
          {items.map((p) => (
            <div
              key={p.id}
              className="glass-light border border-white/5 hover:border-gold/40 transition-all duration-500 group flex flex-col"
              data-testid={`product-card-${p.id}`}
            >
              <div className="relative h-64 overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{ backgroundImage: `url('${p.image}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050C16]/70 via-transparent to-transparent" />
                {p.badge && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-gold text-[#0A192F] px-2 py-1">
                    {p.badge}
                  </span>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">{p.brand}</p>
                <h3 className="text-base text-white mb-3 leading-snug">{p.name}</h3>
                <div className="flex items-center gap-2 text-xs text-white/60 mb-4">
                  <Star size={12} className="text-gold fill-gold" />
                  <span className="text-white">{p.rating}</span>
                  <span>({p.reviews})</span>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <p className="text-gold font-semibold">{p.priceLabel}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-gold"
                    data-testid={`product-inquire-${p.id}`}
                  >
                    Inquire <ArrowRight size={12} className="ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Service body (vendor placeholder data) ------------------------

function ServiceListingBody({ sub }) {
  const vendors = SERVICE_VENDORS[sub.slug] || [];
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
        <p className="text-white/60 text-sm">
          {vendors.length} vetted providers · Direct inquiry
        </p>
      </div>

      {vendors.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="vendor-grid">
          {vendors.map((v) => (
            <div
              key={v.id}
              className="glass-light border border-white/5 hover:border-gold/40 transition-all duration-500 flex flex-col md:flex-row overflow-hidden"
              data-testid={`vendor-card-${v.id}`}
            >
              <div
                className="md:w-60 h-52 md:h-auto flex-shrink-0 bg-cover bg-center"
                style={{ backgroundImage: `url('${v.image}')` }}
              />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3
                    className="text-xl font-medium text-white"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {v.name}
                  </h3>
                  {v.badge && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-2 py-1">
                      {v.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3 inline-flex items-center gap-1">
                  <MapPin size={12} /> {v.location}
                </p>
                <div className="flex items-center gap-2 text-xs text-white/60 mb-5">
                  <Star size={12} className="text-gold fill-gold" />
                  <span className="text-white">{v.rating}</span>
                  <span>({v.reviews} reviews)</span>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <p className="text-gold font-semibold text-sm">{v.priceLabel}</p>
                  <Link to="/contact">
                    <Button
                      size="sm"
                      className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
                      data-testid={`vendor-contact-${v.id}`}
                    >
                      Contact
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyState({ category, sub } = {}) {
  return (
    <div className="glass-light p-12 text-center" data-testid="empty-state">
      <p className="text-white/60 mb-4">No listings match yet — new inventory is added weekly.</p>
      <Link to="/list/new">
        <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
          Be the first to list {sub ? `in ${sub.name}` : 'here'}
        </Button>
      </Link>
    </div>
  );
}
